import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface SmartPaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    indexOfFirst: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (size: number) => void;
    itemLabel?: string;          // default: "items"
    pageSizeOptions?: number[];  // default: [10, 20, 50, 100]
    showJump?: boolean;          // default: true
    showPageCount?: boolean;     // default: true
    showRowsPerPage?: boolean;   // default: true
}

/* ─────────────────────────────────────────────
   SMART PAGINATION COMPONENT
───────────────────────────────────────────── */
const SmartPagination: React.FC<SmartPaginationProps> = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    indexOfFirst,
    onPageChange,
    onItemsPerPageChange,
    itemLabel = "items",
    pageSizeOptions = [10, 20, 50, 100],
    showJump = true,
    showPageCount = true,
    showRowsPerPage = true,
}) => {
    const [jumpValue, setJumpValue] = useState<string>("");
    const [showJumpInput, setShowJumpInput] = useState(false);

    /* ── Ellipsis Logic ── */
    const pageNumbers = useMemo(() => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages: (number | "...")[] = [];
        if (currentPage <= 4) {
            pages.push(1, 2, 3, 4, 5, "...", totalPages);
        } else if (currentPage >= totalPages - 3) {
            pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        } else {
            pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
        }
        return pages;
    }, [currentPage, totalPages]);

    /* ── Jump Handler ── */
    const handleJump = () => {
        const p = parseInt(jumpValue);
        if (!isNaN(p) && p >= 1 && p <= totalPages) {
            onPageChange(p);
            setJumpValue("");
            setShowJumpInput(false);
        } else {
            toast.warning(`Enter a valid page between 1 and ${totalPages}`);
        }
    };

    const startItem = totalItems === 0 ? 0 : indexOfFirst + 1;
    const endItem = Math.min(indexOfFirst + itemsPerPage, totalItems);

    return (
        <div
            className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-4 pt-3"
            style={{ borderTop: "1px solid #e9ecef" }}
        >
            {/* ── Left Side ── */}
            <div className="d-flex align-items-center gap-3 flex-wrap">

                {/* Showing X - Y of Z */}
                <span className="text-muted" style={{ fontSize: "13px" }}>
                    Showing{" "}
                    <strong style={{ color: "#0d6efd" }}>{startItem}</strong>
                    {" – "}
                    <strong style={{ color: "#0d6efd" }}>{endItem}</strong>
                    {" of "}
                    <strong>{totalItems}</strong> {itemLabel}
                </span>

                {/* Rows Per Page */}
                {showRowsPerPage && (
                    <div className="d-flex align-items-center gap-1">
                        <span className="text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                            Rows/page:
                        </span>
                        <select
                            value={itemsPerPage}
                            onChange={e => {
                                onItemsPerPageChange(Number(e.target.value));
                                onPageChange(1);
                            }}
                            style={{
                                padding: "3px 8px",
                                borderRadius: "6px",
                                border: "1px solid #dee2e6",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#0d6efd",
                                background: "#f8f9ff",
                                cursor: "pointer",
                                outline: "none",
                            }}
                        >
                            {pageSizeOptions.map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Total Pages Badge */}
                {showPageCount && (
                    <span style={{
                        fontSize: "11px", fontWeight: 600,
                        padding: "2px 8px", borderRadius: "20px",
                        background: "#e7f1ff", color: "#0d6efd",
                        border: "1px solid #b8d4ff",
                    }}>
                        {totalPages} page{totalPages !== 1 ? "s" : ""}
                    </span>
                )}
            </div>

            {/* ── Right Side ── */}
            <div className="d-flex align-items-center gap-2 flex-wrap">

                {/* Jump to Page */}
                {showJump && totalPages > 5 && (
                    <div className="d-flex align-items-center gap-1">
                        {showJumpInput ? (
                            <>
                                <input
                                    type="number"
                                    min={1}
                                    max={totalPages}
                                    value={jumpValue}
                                    onChange={e => setJumpValue(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") handleJump();
                                        if (e.key === "Escape") {
                                            setShowJumpInput(false);
                                            setJumpValue("");
                                        }
                                    }}
                                    autoFocus
                                    placeholder={`1–${totalPages}`}
                                    style={{
                                        width: "70px", padding: "4px 8px",
                                        borderRadius: "6px",
                                        border: "1.5px solid #0d6efd",
                                        fontSize: "12px", fontWeight: 600,
                                        textAlign: "center", outline: "none",
                                        boxShadow: "0 0 0 3px rgba(13,110,253,0.12)",
                                    }}
                                />
                                <button
                                    onClick={handleJump}
                                    style={{
                                        padding: "4px 10px", borderRadius: "6px",
                                        border: "none", background: "#0d6efd",
                                        color: "#fff", fontSize: "12px",
                                        fontWeight: 600, cursor: "pointer",
                                    }}
                                >
                                    Go
                                </button>
                                <button
                                    onClick={() => { setShowJumpInput(false); setJumpValue(""); }}
                                    style={{
                                        padding: "4px 8px", borderRadius: "6px",
                                        border: "1px solid #dee2e6", background: "#fff",
                                        color: "#6c757d", fontSize: "12px", cursor: "pointer",
                                    }}
                                >
                                    ✕
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setShowJumpInput(true)}
                                title="Jump to page"
                                style={{
                                    padding: "5px 10px", borderRadius: "6px",
                                    border: "1px solid #dee2e6", background: "#fff",
                                    color: "#6c757d", fontSize: "12px", fontWeight: 600,
                                    cursor: "pointer", display: "flex",
                                    alignItems: "center", gap: "4px",
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#0d6efd";
                                    (e.currentTarget as HTMLButtonElement).style.color = "#0d6efd";
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#dee2e6";
                                    (e.currentTarget as HTMLButtonElement).style.color = "#6c757d";
                                }}
                            >
                                <i className="ri-skip-right-line" style={{ fontSize: "14px" }} />
                                Jump
                            </button>
                        )}
                    </div>
                )}

                {/* Page Buttons */}
                <ul className="pagination pagination-sm mb-0"
                    style={{ gap: "3px", display: "flex", flexWrap: "wrap" }}>

                    {/* First */}
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => onPageChange(1)}
                            disabled={currentPage === 1} title="First page"
                            style={{ borderRadius: "6px", border: "1px solid #dee2e6", padding: "4px 9px", fontSize: "13px", fontWeight: 600 }}>
                            «
                        </button>
                    </li>

                    {/* Prev */}
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1} title="Previous"
                            style={{ borderRadius: "6px", border: "1px solid #dee2e6", padding: "4px 9px", fontSize: "13px" }}>
                            ‹
                        </button>
                    </li>

                    {/* Page Numbers */}
                    {pageNumbers.map((page, i) =>
                        page === "..." ? (
                            <li key={`ellipsis-${i}`} className="page-item disabled">
                                <span className="page-link"
                                    style={{
                                        borderRadius: "6px", border: "1px solid #dee2e6",
                                        padding: "4px 9px", fontSize: "13px",
                                        color: "#adb5bd", cursor: "default", userSelect: "none",
                                    }}>
                                    …
                                </span>
                            </li>
                        ) : (
                            <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                                <button
                                    className="page-link"
                                    onClick={() => onPageChange(page as number)}
                                    style={{
                                        borderRadius: "6px",
                                        border: currentPage === page ? "1px solid #0d6efd" : "1px solid #dee2e6",
                                        padding: "4px 9px", fontSize: "13px",
                                        fontWeight: currentPage === page ? 700 : 400,
                                        background: currentPage === page
                                            ? "linear-gradient(135deg,#0d6efd,#6610f2)"
                                            : "#fff",
                                        color: currentPage === page ? "#fff" : "#495057",
                                        boxShadow: currentPage === page
                                            ? "0 2px 8px rgba(13,110,253,0.35)"
                                            : "none",
                                        transition: "all 0.2s",
                                        minWidth: "32px",
                                        textAlign: "center",
                                    }}
                                >
                                    {page}
                                </button>
                            </li>
                        )
                    )}

                    {/* Next */}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages} title="Next"
                            style={{ borderRadius: "6px", border: "1px solid #dee2e6", padding: "4px 9px", fontSize: "13px" }}>
                            ›
                        </button>
                    </li>

                    {/* Last */}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages} title="Last page"
                            style={{ borderRadius: "6px", border: "1px solid #dee2e6", padding: "4px 9px", fontSize: "13px", fontWeight: 600 }}>
                            »
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default SmartPagination;